import { useSelector } from '@/api/state';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import TypeToSearchField, {
  TypeToSearchRenderOptionProps,
} from '@payaca/components/typeToSearchField/TypeToSearchField';
import { getAddressAsString } from '@payaca/helpers/locationHelper';
import * as validationActions from '@payaca/store/validation/validationActions';
import {
  LoqateAddressResult,
  LoqateAddressResultType,
  RetrievedAddress,
} from '@payaca/types/addressTypes';
import { clsx } from '@payaca/utilities/cvaMerge';
import { FC, useCallback, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';

import UntitledIcon from '@payaca/untitled-icons';
interface Props {
  inputStyleVariant?: InputStyleVariant;
  label?: string;
  onChange: (address: { [key: string]: any }) => void;
  addressFieldName?: string;
  onEnterManually?: () => void;
  placeholder?: string;
}

const AddressLookupControl: FC<Props> = ({
  inputStyleVariant,
  label,
  onChange,
  addressFieldName,
  onEnterManually,
  placeholder,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const account = useAccount();
  const region = useRegion();

  const [addressLookupResults, setAddressLookupResults] =
    useState<LoqateAddressResult[]>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [disableNoOptionsText, setDisableNoOptionsText] = useState(true);

  const isLookingUpAddressBySearchTerm = useSelector(
    (state) => state.validation.isLookingUpAddressBySearchTerm
  );

  const addressOptions = useMemo(() => {
    let options = addressLookupResults || [];
    if (onEnterManually) {
      options = [...options, { id: '', label: 'Enter manually...' }];
    }
    return options;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'onEnterManually'
  }, [addressLookupResults]);

  const lookupAddress = useCallback(
    (_searchTerm: string, containerId = '') => {
      setDisableNoOptionsText(!_searchTerm);
      setSearchTerm(_searchTerm);
      dispatch(
        validationActions.requestLookupAddressBySearchTerm(
          _searchTerm,
          containerId,
          region,
          (error: Error, response: any) => {
            setAddressLookupResults(response);
          }
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [account]
  );

  const renderAddressLookupOption = useCallback(
    (
      addressLookupOption: LoqateAddressResult,
      props: TypeToSearchRenderOptionProps
    ) => {
      let label = addressLookupOption.label;
      let subLabel = null;
      if (addressLookupOption.type !== LoqateAddressResultType.ADDRESS) {
        const splitLabel = label.split(' - ');
        label = splitLabel[0];
        subLabel = splitLabel[1];
      }
      return (
        <li
          {...props}
          className={clsx('address-lookup-option', props.className)}
        >
          <div>
            <span>{label}</span>
            {subLabel && <span className="sub-label">{subLabel}</span>}
          </div>
          {addressLookupOption.type !== LoqateAddressResultType.ADDRESS && (
            <span>
              <UntitledIcon name="chevron-right" />
            </span>
          )}
        </li>
      );
    },
    []
  );

  const keyName = (fieldName: string) =>
    addressFieldName ? `${addressFieldName}.${fieldName}` : fieldName;

  const selectAddressFromLookup = useCallback(
    (addressResult?: LoqateAddressResult) => {
      if (!addressResult) return;
      if (!addressResult?.id && !!onEnterManually) {
        // Selected "Enter manually"
        return onEnterManually();
      }
      if (addressResult.type === LoqateAddressResultType.ADDRESS) {
        // selected an address - retrieve full address and set
        dispatch(
          validationActions.requestRetrieveAddress(
            addressResult.id,
            (error: Error, completeAddress: RetrievedAddress) => {
              const { line1, line2, city, administrativeArea, postcode } =
                completeAddress;
              onChange({
                [keyName('line1')]: line1,
                [keyName('line2')]: line2,
                [keyName('city')]: city,
                [keyName('administrativeArea')]: administrativeArea,
                [keyName('postcode')]: postcode,
                [keyName('countryName')]: completeAddress.country,
              });
              setAddressLookupResults(undefined);
              const address =
                getAddressAsString({
                  line1,
                  line2,
                  city,
                  administrativeArea,
                  postcode,
                }) || '';
              setSearchTerm(address);
              setDisableNoOptionsText(true);
            }
          )
        );
      } else {
        // selected a postcode with multiple addresses - lookup address result to get nested addresses
        lookupAddress(searchTerm, addressResult.id);
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: addressFieldName
    [
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on `keyName`, which changes every render
      keyName,
      onChange,
      addressFieldName,
      lookupAddress,
      onEnterManually,
      searchTerm,
    ]
  );

  return (
    <div className="address-lookup-control">
      <TypeToSearchField
        disableCloseOnSelect={true}
        label={label}
        styleVariant={inputStyleVariant}
        onSearchTermChange={lookupAddress}
        options={addressOptions}
        isLoadingOptions={isLookingUpAddressBySearchTerm}
        renderOption={renderAddressLookupOption}
        disableFilteringInComponent={true}
        onSelectOption={selectAddressFromLookup}
        placeholder={placeholder}
        noOptionsText={
          !disableNoOptionsText
            ? "We couldn't find any addresses matching your search."
            : undefined
        }
        value={searchTerm}
        blockInputChangeReasons={['reset', 'clear']}
      />
    </div>
  );
};

export default AddressLookupControl;
